import { FC, useEffect, useState } from 'react';
import { Button, Modal } from 'antd';
import { isArray, isNotEmpty, refactorSelection } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { getChancePointList, getChancepointSelection } from '@/common/api/expandStore/chancepoint';
import { COMPARE_MAX_COUNT, COMPARE_STATUS_COLOR } from '../../compare-config';
import styles from './index.module.less';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import V2SearchForm from '@/common/components/Form/V2SearchForm';
import V2Table from '@/common/components/Data/V2Table';

interface ComparePointModalProps {
  /** 当前报告对应的机会点 ID，作为对比基准且不可取消选中 */
  chancePointId: number;
  /** 已选中的对比点位 ID，用于从对比抽屉再次打开时回显 */
  selectedIds?: number[];
  /** 取消/关闭弹窗 */
  onCancel: () => void;
  /** 确认选择，按基准在前的顺序抛出点位 ID */
  onOk: (ids: number[]) => void;
}

/**
 * @description 选择对比点位弹窗
 * 内部复用机会点列表接口，基准点位取接口 meta.extraChancePoints 并固定展示在第一页第一项
 */
const ComparePointModal: FC<ComparePointModalProps> = ({
  chancePointId, // 当前报告的机会点，作为不可取消的对比基准
  selectedIds, // 已选对比点位，用于从对比抽屉再次打开时回显
  onCancel,
  onOk,
}) => {
  const [filters, setFilters] = useState<{ keyword?: string; statuses?: number[] }>({});
  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>(() => {
    const initialIds = isArray(selectedIds) ? selectedIds : [];
    return Array.from(new Set([Number(chancePointId), ...initialIds.map((id) => Number(id))]));
  });

  const methods = useMethods({
    /**
     * @description 加载机会点状态下拉选项，复用机会点管理页的取数逻辑
     */
    loadStatusOptions() {
      getChancepointSelection({}, { needCancel: false }).then((res: any) => {
        const status = res?.status;
        setStatusOptions(isArray(status) ? refactorSelection(status) : []);
      }, (error: any) => {
        console.log(error);
      });
    },
    /**
     * @description 加载机会点列表
     * 第一页将接口单独返回的基准点位插入首位，其余页面只展示分页列表
     */
    loadPointList(params?: any) {
      const page = Number(params?.page) || 1;
      return getChancePointList({
        page,
        size: params?.size,
        keyword: filters.keyword || undefined,
        statuses: isArray(filters.statuses) && filters.statuses.length ? filters.statuses : undefined,
        // 基准点位不参与筛选，由接口单独返回，避免被检索条件过滤掉
        extraChancePointIds: [Number(chancePointId)],
      }).then((result: any) => {
        const list = isArray(result?.objectList) ? result.objectList : [];
        const extra = result?.meta?.extraChancePoints;
        const extraList = isArray(extra) ? extra : isNotEmpty(extra) ? [extra] : [];
        const baseline = extraList.find((item: any) => Number(item?.id) === Number(chancePointId));
        const normalList = list.filter((item: any) => Number(item?.id) !== Number(chancePointId));
        return {
          dataSource: page === 1 && baseline ? [baseline, ...normalList] : normalList,
          count: Number(result?.totalNum) || 0,
        };
      }, (error: any) => {
        console.log(error);
        return { dataSource: [], count: 0 };
      });
    },
    /**
     * @description 接收筛选条件并触发列表重新请求
     */
    handleSearch(values: any) {
      const keyword = typeof values?.keyword === 'string' ? values.keyword.trim() : '';
      const statuses = isArray(values?.statuses) ? values.statuses.map((status: any) => Number(status)) : [];
      setFilters({
        keyword: keyword || undefined,
        statuses,
      });
    },
    /**
     * @description 表格勾选变化
     * 基准点位始终保留并排在最前，新增点位追加在已选点位之后，最多选择 5 个
     */
    handleSelectChange(keys: (string | number)[]) {
      const baselineId = Number(chancePointId);
      const normalizedKeys = keys.map((key) => Number(key));
      const keySet = new Set(normalizedKeys);
      const keptKeys = selectedRowKeys.filter((id) => keySet.has(id) && id !== baselineId);
      const addedKeys = normalizedKeys.filter((id) => !selectedRowKeys.includes(id) && id !== baselineId);
      const nextKeys = [baselineId, ...keptKeys, ...addedKeys];
      if (nextKeys.length > COMPARE_MAX_COUNT) {
        V2Message.warning(`最多选择${COMPARE_MAX_COUNT}个对比点位`);
        return;
      }
      setSelectedRowKeys(nextKeys);
    },
    /**
     * @description 基准点位禁用勾选，且默认保持选中
     */
    getCheckboxProps(record: any) {
      return {
        disabled: Number(record?.id) === Number(chancePointId),
      };
    },
    /**
    //  * @description 斑马纹行样式
    //  */
    // getRowClassName(_record: any, index: number) {
    //   return index % 2 === 1 ? styles.oddRow : '';
    // },
    /**
     * @description 渲染点位名称，基准点位追加“基准”标签
     */
    renderPointName(value: any, record: any) {
      const isBaseline = Number(record?.id) === Number(chancePointId);
      return (
        <span className={styles.pointName}>
          <span
            className={styles.pointNameText}
            title={isNotEmpty(value) ? String(value) : '-'}
          >
            {isNotEmpty(value) ? value : '-'}
          </span>
          {isBaseline && <span className={styles.baseTag}>基准</span>}
        </span>
      );
    },
    /**
     * @description 渲染机会点状态，颜色规则与移动端 getStatusColor 保持一致
     */
    renderStatus(value: any, record: any) {
      const color = COMPARE_STATUS_COLOR[Number(record?.status)] || '#999999';
      return (
        <span className={styles.statusCell}>
          <span
            className={styles.statusDot}
            style={{ backgroundColor: color }}
          />
          <span className={styles.statusText}>{isNotEmpty(value) ? value : '-'}</span>
        </span>
      );
    },
    /**
     * @description 取消选择，父级卸载弹窗即可清空筛选项与勾选状态
     */
    handleCancel() {
      onCancel();
    },
    /**
     * @description 确认选择，基准点位固定排在第一位
     */
    handleOk() {
      const baselineId = Number(chancePointId);
      const ids = [baselineId, ...selectedRowKeys.filter((id) => id !== baselineId)];
      if (ids.length < 2) {
        V2Message.warning('请至少选择1个对比点位');
        return;
      }
      onOk(ids);
    },
  });

  useEffect(() => {
    methods.loadStatusOptions();
  }, []);

  const columns = [
    {
      title: '点位名称',
      key: 'name',
      dataIndex: 'name',
      render: methods.renderPointName,
    },
    {
      title: '状态',
      key: 'statusName',
      dataIndex: 'statusName',
      width: 180,
      render: methods.renderStatus,
    },
  ];

  return (
    <Modal
      open
      title='选择对比点位'
      width={720}
      onCancel={methods.handleCancel}
      className={styles.compareModal}
      footer={[
        <Button
          key='cancel'
          onClick={methods.handleCancel}
        >
          取消
        </Button>,
        <Button
          key='ok'
          type='primary'
          disabled={selectedRowKeys.length < 2}
          onClick={methods.handleOk}
        >
          确定
        </Button>,
      ]}
    >
      <V2SearchForm
        labelLength={4}
        labelAlign='right'
        colon={false}
        className={styles.filterForm}
        onSearch={methods.handleSearch}
      >
        <V2FormInput
          label=''
          name='keyword'
          placeholder='输入点位名称'
        />
        <V2FormSelect
          label=''
          name='statuses'
          placeholder='选择状态'
          options={statusOptions}
          config={{
            mode: 'multiple',
            maxTagCount: 'responsive',
            style: {
              width: '190px'
            }
          }}
        />
      </V2SearchForm>
      <V2Table
        rowKey='id'
        filters={filters}
        defaultColumns={columns}
        onFetch={methods.loadPointList}
        rowSelection={{
          selectedRowKeys,
          onChange: methods.handleSelectChange,
          getCheckboxProps: methods.getCheckboxProps,
          type: 'checkbox',
          hideSelectAll: true,
          // 跨页勾选时需要保留非当前页的点位，否则翻页后已选点位会被丢弃
          preserveSelectedRowKeys: true,
        }}
        pageSize={10}
        paginationConfig={{ showSizeChanger: false }}
        /* 基准点位始终选中，V2Table 会按已选状态预留 40px，这里补足表格滚动高度 */
        scroll={{ y: 340 }}
        // bordered={false}
        hideColumnPlaceholder
        showBatchOperate={false}
        emptyRender
        // className={styles.pointTable}
        // rowClassName={methods.getRowClassName}
      />
    </Modal>
  );
};

export default ComparePointModal;
